import { useNavigate, useSearch } from "@tanstack/react-router"
import React, { useEffect, useState } from "react"

import { trpc } from "../../lib/trpc"

interface Campaign {
  id: string
  name: string
  description?: string
  status: "draft" | "active" | "paused" | "completed"
  campaignContext?: string
  objectives?: string[]
}

interface Contact {
  id: string
  name?: string
  email?: string
  phone?: string
  company?: string
  quality: "hot" | "warm" | "cold"
  primaryPlatform?: string
}

interface Template {
  id: string
  name: string
  content: string
  category: string
  variables: {
    name: string
    description?: string
    required: boolean
    defaultValue?: string
  }[]
}

type Platform = "email" | "sms" | "whatsapp" | "messenger"

const platformOptions: { value: Platform; label: string; icon: string }[] = [
  { value: "email", label: "Email", icon: "📧" },
  { value: "sms", label: "SMS", icon: "💬" },
  { value: "whatsapp", label: "WhatsApp", icon: "💚" },
  { value: "messenger", label: "Messenger", icon: "💙" },
]

const QualityBadge: React.FC<{ quality: string }> = ({ quality }) => {
  const colors = {
    hot: "bg-red-100 text-destructive dark:bg-red-800 dark:text-red-300",
    warm: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-300",
    cold: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300",
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[quality as keyof typeof colors]}`}>
      {quality.charAt(0).toUpperCase() + quality.slice(1)}
    </span>
  )
}

const StartConversation: React.FC = () => {
  const navigate = useNavigate()
  const search = useSearch({ from: "/crm/start-conversation" })
  const [selectedCampaign, setSelectedCampaign] = useState<string>("")
  const [selectedContact, setSelectedContact] = useState<string>("")
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>("email")
  const [useTemplate, setUseTemplate] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string>("")
  const [customMessage, setCustomMessage] = useState("")
  const [templateData, setTemplateData] = useState<Record<string, string>>({})
  const [contactSearch, setContactSearch] = useState("")
  const [campaignSearch, setCampaignSearch] = useState("")

  // Query active campaigns
  const { data: campaignsData, isLoading: campaignsLoading } = trpc.crm.campaigns.list.useQuery({
    status: "active",
    search: campaignSearch || undefined,
    limit: 20,
  })

  // Query contacts
  const { data: contactsData, isLoading: contactsLoading } = trpc.crm.contacts.list.useQuery({
    search: contactSearch || undefined,
    limit: 50,
  })

  // Query templates for initial outreach
  const { data: templatesData, isLoading: templatesLoading } = trpc.crm.templates.list.useQuery({
    category: "initial_outreach",
    platform: selectedPlatform,
    limit: 20,
  })

  // Get selected template details
  const { data: templateDetails } = trpc.crm.templates.getById.useQuery(
    { id: selectedTemplate },
    { enabled: !!selectedTemplate },
  )

  // Start conversation mutation
  const startConversationMutation = trpc.crm.conversations.start.useMutation({
    onSuccess: (conversation) => {
      navigate({ to: `/crm/inbox/${conversation.id}` })
    },
    onError: (error) => {
      alert(`Error starting conversation: ${error.message}`)
    },
  })

  // Auto-select contact from URL params
  useEffect(() => {
    if (search?.contactId && !selectedContact) {
      setSelectedContact(search.contactId)
    }
  }, [search?.contactId, selectedContact])

  const selectedCampaignData = campaignsData?.campaigns?.find((c) => c.id === selectedCampaign)
  const selectedContactData = contactsData?.contacts?.find((c) => c.id === selectedContact)

  const handleStartConversation = async () => {
    if (!selectedCampaign || !selectedContact) {
      alert("Please select both a campaign and a contact")
      return
    }

    let initialMessage = ""

    if (useTemplate && selectedTemplate && templateDetails) {
      // Replace template variables with user data
      initialMessage = templateDetails.content
      Object.entries(templateData).forEach(([key, value]) => {
        const regex = new RegExp(`{{${key}}}`, "g")
        initialMessage = initialMessage.replace(regex, value)
      })

      // Auto-fill some variables from contact data
      if (selectedContactData) {
        initialMessage = initialMessage.replace(/{{name}}/g, selectedContactData.name || "there")
        initialMessage = initialMessage.replace(/{{company}}/g, selectedContactData.company || "")
        initialMessage = initialMessage.replace(/{{email}}/g, selectedContactData.email || "")
      }
    } else if (customMessage.trim()) {
      initialMessage = customMessage
    }

    try {
      await startConversationMutation.mutateAsync({
        campaignId: selectedCampaign,
        contactId: selectedContact,
        platform: selectedPlatform,
        initialMessage: initialMessage || undefined,
      })
    } catch (error) {
      console.error("Failed to start conversation:", error)
    }
  }

  const getContactDisplayName = (contact: Contact) => {
    return contact.name || contact.email || contact.phone || "Unknown Contact"
  }

  const isFormValid = selectedCampaign && selectedContact

  return (
    <div className="h-full overflow-auto bg-foregroundx ">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold  dark:text-white">Start New Conversation</h1>
              <p className="mt-2  dark:text-gray-400">Select a campaign and contact to begin a conversation</p>
            </div>
            <button
              onClick={() => navigate({ to: "/crm/inbox" })}
              className=" hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ← Back to Inbox
            </button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Selection */}
            <div className="space-y-6">
              {/* Campaign Selection */}
              <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
                <h2 className="text-lg font-semibold  dark:text-white mb-4">1. Select Campaign</h2>

                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search campaigns..."
                    value={campaignSearch}
                    onChange={(e) => setCampaignSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                  />
                </div>

                {campaignsLoading ? (
                  <div className="text-gray-500">Loading campaigns...</div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {campaignsData?.campaigns?.map((campaign) => (
                      <div
                        key={campaign.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedCampaign === campaign.id
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                            : "border-border dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                        }`}
                        onClick={() => setSelectedCampaign(campaign.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium  dark:text-white">{campaign.name}</div>
                            <div className="text-sm  dark:text-gray-400">
                              {campaign.description || campaign.campaignContext || "No description"}
                            </div>
                          </div>
                          <span className="text-xs text-green-600 bg-green-100 dark:bg-green-800 dark:text-green-300 px-2 py-1 rounded">
                            Active
                          </span>
                        </div>
                      </div>
                    ))}

                    {campaignsData?.campaigns?.length === 0 && (
                      <div className="text-center py-4">
                        <div className="text-gray-500 mb-2">No active campaigns found</div>
                        <button
                          onClick={() => navigate({ to: "/crm/campaigns" })}
                          className=" hover:text-blue-800 text-sm"
                        >
                          Create a campaign first
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contact Selection */}
              <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
                <h2 className="text-lg font-semibold  dark:text-white mb-4">2. Select Contact</h2>

                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                  />
                </div>

                {contactsLoading ? (
                  <div className="text-gray-500">Loading contacts...</div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {contactsData?.contacts?.map((contact) => (
                      <div
                        key={contact.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedContact === contact.id
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                            : "border-border dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                        }`}
                        onClick={() => setSelectedContact(contact.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium  dark:text-white">{getContactDisplayName(contact as any)}</div>
                            <div className="text-sm  dark:text-gray-400">
                              {contact.company && `${contact.company} • `}
                              {contact.email || contact.phone}
                            </div>
                          </div>
                          <QualityBadge quality={contact.quality} />
                        </div>
                      </div>
                    ))}

                    {contactsData?.contacts?.length === 0 && (
                      <div className="text-center py-4">
                        <div className="text-gray-500 mb-2">No contacts found</div>
                        <button
                          onClick={() => navigate({ to: "/crm/contacts" })}
                          className=" hover:text-blue-800 text-sm"
                        >
                          Add contacts first
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Platform Selection */}
              <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
                <h2 className="text-lg font-semibold  dark:text-white mb-4">3. Select Platform</h2>

                <div className="grid grid-cols-2 gap-3">
                  {platformOptions.map((platform) => (
                    <button
                      key={platform.value}
                      onClick={() => setSelectedPlatform(platform.value)}
                      className={`p-3 border rounded-lg flex items-center gap-2 transition-colors ${
                        selectedPlatform === platform.value
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                          : "border-border dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-primary dark:text-gray-300"
                      }`}
                    >
                      <span className="text-lg">{platform.icon}</span>
                      <span className="font-medium">{platform.label}</span>
                    </button>
                  ))}
                </div>

                {selectedContactData?.primaryPlatform && (
                  <div className="mt-3 text-sm  dark:text-gray-400">
                    💡 This contact&apos;s preferred platform: {selectedContactData.primaryPlatform}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Message */}
            <div className="space-y-6">
              {/* Message Composition */}
              <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
                <h2 className="text-lg font-semibold  dark:text-white mb-4">4. Initial Message (Optional)</h2>

                <div className="space-y-4">
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="messageType"
                        checked={!useTemplate}
                        onChange={() => setUseTemplate(false)}
                        className="mr-2"
                      />
                      <span className="text-primary dark:text-gray-300">Custom Message</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="messageType"
                        checked={useTemplate}
                        onChange={() => setUseTemplate(true)}
                        className="mr-2"
                      />
                      <span className="text-primary dark:text-gray-300">Use Template</span>
                    </label>
                  </div>

                  {useTemplate ? (
                    <div className="space-y-4">
                      {/* Template Selection */}
                      <div>
                        <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-2">
                          Select Template
                        </label>
                        <select
                          value={selectedTemplate}
                          onChange={(e) => setSelectedTemplate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                        >
                          <option value="">Choose a template...</option>
                          {templatesData?.templates?.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Template Variables */}
                      {templateDetails?.variables &&
                        Array.isArray(templateDetails.variables) &&
                        templateDetails.variables.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-2">
                              Template Variables
                            </label>
                            <div className="space-y-2">
                              {templateDetails.variables.map((variable) => (
                                <div key={variable.name}>
                                  <input
                                    type="text"
                                    placeholder={`${variable.name}${variable.required ? " (required)" : ""}`}
                                    value={templateData[variable.name] || ""}
                                    onChange={(e) =>
                                      setTemplateData((prev) => ({
                                        ...prev,
                                        [variable.name]: e.target.value,
                                      }))
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                                  />
                                  {variable.description && (
                                    <div className="text-xs text-gray-500 mt-1">{variable.description}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Template Preview */}
                      {templateDetails && (
                        <div>
                          <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-2">
                            Preview
                          </label>
                          <div className="p-3 bg-foregroundx dark:bg-gray-700 rounded-lg border text-primary dark:text-gray-300 whitespace-pre-wrap">
                            {templateDetails.content.replace(/{{(\w+)}}/g, (match, key) => {
                              if (key === "name" && selectedContactData?.name) return selectedContactData.name
                              if (key === "company" && selectedContactData?.company) return selectedContactData.company
                              if (key === "email" && selectedContactData?.email) return selectedContactData.email
                              return templateData[key] || match
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-2">
                        Write your message
                      </label>
                      <textarea
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder="Hi there! I wanted to reach out about..."
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white resize-none"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Summary & Action */}
              <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
                <h2 className="text-lg font-semibold  dark:text-white mb-4">Summary</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className=" dark:text-gray-400">Campaign:</span>
                    <span className=" dark:text-white font-medium">{selectedCampaignData?.name || "Not selected"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className=" dark:text-gray-400">Contact:</span>
                    <span className=" dark:text-white font-medium">
                      {selectedContactData ? getContactDisplayName(selectedContactData as any) : "Not selected"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className=" dark:text-gray-400">Platform:</span>
                    <span className=" dark:text-white font-medium">
                      {platformOptions.find((p) => p.value === selectedPlatform)?.label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className=" dark:text-gray-400">Initial Message:</span>
                    <span className=" dark:text-white font-medium">
                      {useTemplate && selectedTemplate ? "Template" : customMessage.trim() ? "Custom" : "None"}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleStartConversation}
                  disabled={!isFormValid || startConversationMutation.isLoading}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    isFormValid && !startConversationMutation.isLoading
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {startConversationMutation.isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                      Starting Conversation...
                    </span>
                  ) : (
                    "Start Conversation"
                  )}
                </button>

                {!isFormValid && (
                  <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center">
                    Please select a campaign and contact to continue
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StartConversation
